import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';

import Remove2Icon from 'zignal-landscape/lib/Icon/Remove2Icon';
import Save2Icon from 'zignal-landscape/lib/Icon/Save2Icon';
import themeable from 'zignal-landscape/lib/themeable';

import StoryCheckerDetailHighlightContent from './components/StoryCheckerDetailHighlightContent';
import StoryCheckerDetailHighlightMatch from './components/StoryCheckerDetailHighlightMatch';
import { deriveTagCountsFromMarkup } from './util/markupTransformer';

const ICON_PROPS = {
  width: '1em',
  height: '1em',
};

const totalTagCounts = (body, title) => {
  const counts = deriveTagCountsFromMarkup(body);
  if (title) {
    Object.entries(deriveTagCountsFromMarkup(title)).forEach(
      ([key, count]) => { counts[key] += count; }
    );
  }
  return counts;
};

function StoryCheckerDetail(props) {
  const {
    styles,
    body, title, url = '', accountName = '', campaignName = '', profileName = '',
  } = props;
  const { includedCount, excludedByProfileCount, excludedByAccountCount } = totalTagCounts(body, title);
  const doesMatchProfile = (includedCount !== 0) && ((excludedByProfileCount + excludedByAccountCount) === 0);

  const urlFieldValue = (isEmpty(url)
    ? ''
    : (<div className={styles.detailSummaryLine}>
      <b>URL:</b> {url}
    </div>)
  );

  const analysis = [];
  if (includedCount === 0) {
    analysis.push(<li key="included">
      <span className={styles.detailIconRed}><Remove2Icon {...ICON_PROPS} /></span>
      <span className={styles.detailAnalysisText}>
        This story did not match because it has no included terms.
      </span>
    </li>);
  }
  else {
    analysis.push(<li key="included">
      <span className={styles.detailIconGreen}><Save2Icon {...ICON_PROPS} /></span>
      <span className={styles.detailAnalysisText}>
        This story matched because it has included terms.
      </span>
    </li>);

    // you can't get to the exclusion phase without at least one inclusion
    if (excludedByProfileCount === 0) {
      analysis.push(<li key="excludedByProfile">
        <span className={styles.detailIconGreen}><Save2Icon {...ICON_PROPS} /></span>
        <span className={styles.detailAnalysisText}>
          This story matched because it has no excluded terms.
        </span>
      </li>);
    }
    else {
      analysis.push(<li key="excludedByProfile">
        <span className={styles.detailIconRed}><Remove2Icon {...ICON_PROPS} /></span>
        <span className={styles.detailAnalysisText}>
          This story did not match because it has excluded terms.
        </span>
      </li>);
    }
    if (excludedByAccountCount === 0) {
      analysis.push(<li key="excludedByAccount">
        <span className={styles.detailIconGreen}><Save2Icon {...ICON_PROPS} /></span>
        <span className={styles.detailAnalysisText}>
          This story matched because it has no account level excluded terms.
        </span>
      </li>);
    }
    else {
      analysis.push(<li key="excludedByAccount">
        <span className={styles.detailIconRed}><Remove2Icon {...ICON_PROPS} /></span>
        <span className={styles.detailAnalysisText}>
          This story did not match because it has account level excluded terms.
        </span>
      </li>);
    }
  }


  return (<div className={styles.StoryCheckerDetail}>
    <div className={styles.detailExplanation}>
      The included and/or excluded terms of the story are highlighted below. <StoryCheckerDetailHighlightMatch
        styles={styles}
        text="Yellow"
        nestingDepth={0}
        showTooltip={false}
        included
      >Yellow</StoryCheckerDetailHighlightMatch> highlighted text means it is an included dataset term
      and <StoryCheckerDetailHighlightMatch
        styles={styles}
        text="red"
        nestingDepth={0}
        showTooltip={false}
        excludedByProfile
      >red</StoryCheckerDetailHighlightMatch> highlighted text means it is an excluded dataset term.
    </div>

    <hr />

    <div className={styles.detailSummary}>
      {urlFieldValue}
      <div className={styles.detailSummaryLine}>
        <b>Workspace:</b> {campaignName}
      </div>
      <div className={styles.detailSummaryLine}>
        <b>{doesMatchProfile ? 'Matches' : 'Does not match'} dataset:</b> {profileName}
        <ul className={styles.detailAnalysis}>
          {analysis}
        </ul>
      </div>
    </div>

    <hr />

    {title && <div className={styles.detailContent}>
      <b>Story Title</b>

      <StoryCheckerDetailHighlightContent
        styles={styles}
        markup={title}
        accountName={accountName}
        profileName={profileName}
      />
    </div>}

    <div className={styles.detailContent}>
      <b>Story Content</b>

      <StoryCheckerDetailHighlightContent
        styles={styles}
        markup={body}
        accountName={accountName}
        profileName={profileName}
      />
    </div>
  </div>);
}

StoryCheckerDetail.propTypes = {
  styles: PropTypes.object,
  body: PropTypes.string.isRequired,
  title: PropTypes.string,
  url: PropTypes.string,
  accountName: PropTypes.string,
  campaignName: PropTypes.string,
  profileName: PropTypes.string,
};


export default themeable(StoryCheckerDetail);
